import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getPendingReportsByDoctor,
  getPatientById,
  getAllPendingReports
} from "@/data/mockData";
import {
  FileText,
  Search,
  Filter,
  AlertTriangle,
  Clock,
  User,
  ChevronRight,
  Calendar
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { it } from "date-fns/locale";

export const DoctorPendingReports = () => {
  const navigate = useNavigate();
  const { currentDoctor } = useOutletContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");

  const allReports = getPendingReportsByDoctor(currentDoctor.id);
  
  // Apply filters
  const filteredReports = allReports.filter(report => {
    const patient = getPatientById(report.patientId);
    const matchesSearch = 
      patient?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.serviceName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = priorityFilter === "all" || report.priority === priorityFilter;
    const matchesService = serviceFilter === "all" || report.serviceId === serviceFilter;
    return matchesSearch && matchesPriority && matchesService;
  });

  // Get unique services for filter
  const services = [...new Set(allReports.map(r => r.serviceId))];

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" />In attesa</Badge>;
      case "in_review":
        return <Badge className="bg-info/10 text-info border-info/20 gap-1"><FileText className="h-3 w-3" />In revisione</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Esami da Refertare</h1>
        <p className="text-muted-foreground">Gestisci gli esami in attesa di referto</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca paziente o esame..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Priorità" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le priorità</SelectItem>
                <SelectItem value="urgent">Urgenti</SelectItem>
                <SelectItem value="normal">Normali</SelectItem>
              </SelectContent>
            </Select>
            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Tipo esame" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli esami</SelectItem>
                {services.map(serviceId => {
                  const report = allReports.find(r => r.serviceId === serviceId);
                  return (
                    <SelectItem key={serviceId} value={serviceId}>
                      {report?.serviceName}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {filteredReports.length} esami in attesa
        </p>
        {filteredReports.some(r => r.priority === 'urgent') && (
          <Badge variant="destructive" className="gap-1">
            <AlertTriangle className="h-3 w-3" />
            {filteredReports.filter(r => r.priority === 'urgent').length} urgenti
          </Badge>
        )}
      </div>

      {/* Reports List */}
      <ScrollArea className="h-[500px]">
        <div className="space-y-4 pr-4">
          {filteredReports.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">Nessun esame in attesa</p>
              </CardContent>
            </Card>
          ) : (
            filteredReports.map((report, index) => {
              const patient = getPatientById(report.patientId);
              return (
                <Card
                  key={report.id}
                  className={cn(
                    "cursor-pointer transition-all hover:shadow-lg animate-fade-in-up",
                    report.priority === 'urgent' && "border-destructive/50 bg-destructive/5"
                  )}
                  style={{ animationDelay: `${index * 50}ms` }}
                  onClick={() => navigate(`/doctor/report/${report.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {report.priority === 'urgent' && (
                            <Badge variant="destructive" className="gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              Urgente
                            </Badge>
                          )}
                          {getStatusBadge(report.status)}
                          <Badge variant="outline">{report.serviceName}</Badge>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="font-semibold">{patient?.name}</span>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {patient?.fiscalCode}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          <span>
                            Esame del {format(new Date(report.examDate), "d MMMM yyyy 'alle' HH:mm", { locale: it })}
                          </span>
                        </div>
                      </div>
                      
                      <Button variant="ghost" size="icon">
                        <ChevronRight className="h-5 w-5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default DoctorPendingReports;
